import type { DefaultAlgoliaCustomData, JobAdsResult } from "@/modules/jobAds/client/api/types"

import { useQuery } from "@tanstack/react-query"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { getJobAdsFromAlgolia } from "@/modules/jobAds/client/api/getJobAdsFromAlgolia"
import { getJobAdsFromAlgoliaQueryKeys } from "@/modules/jobAds/client/api/getJobAdsFromAlgoliaQueryKeys"
import { QUERY_CACHE_TIME, QUERY_STALE_TIME } from "@/modules/jobAds/client/constants"
import { useComputeAlgoliaJobAdsRequest } from "@/modules/jobAds/client/hooks/useComputeAlgoliaJobAdsRequest"
import { computeJobAdsResultFromAlgolia } from "@/modules/jobAds/client/utils/computeJobAdsResultFromAlgolia"
import {
  ALGOLIA_MULTI_HOSTS_UNLEASH_TOGGLE,
  DIRECT_ALGOLIA_CONNECTION_UNLEASH_TOGGLE,
  SEARCH_PROXY_V3_CONNECTION_UNLEASH_TOGGLE,
} from "@/modules/search/flags"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"
import { useCurrentUserSearchConfigFetch } from "@/modules/user/client/hooks/useCurrentUserSearchConfigFetch"

export type JobAdsFetch<CustomData extends DefaultAlgoliaCustomData> = {
  dataJobAds?: JobAdsResult<CustomData>
  isErrorJobAds: boolean
  isLoadingJobAds: boolean
  isSuccessJobAds: boolean
}

export const useJobAdsFromAlgolia = <CustomData extends DefaultAlgoliaCustomData>(): JobAdsFetch<CustomData> => {
  const { dataCurrentUser: currentUser } = useCurrentUserFetch()
  const { dataCareerCenter: careerCenter } = useCareerCenterFetch()
  const { dataCurrentUserSearchConfig: currentSearchConfig } = useCurrentUserSearchConfigFetch()
  const algoliaJobAdsRequest = useComputeAlgoliaJobAdsRequest()
  const isDirectAlgoliaConnectionEnabled = useFlag(DIRECT_ALGOLIA_CONNECTION_UNLEASH_TOGGLE)
  const isSearchProxyV3ConnectionEnabled = useFlag(SEARCH_PROXY_V3_CONNECTION_UNLEASH_TOGGLE)
  const isMultiHostConnectionEnabled = useFlag(ALGOLIA_MULTI_HOSTS_UNLEASH_TOGGLE)

  const {
    data,
    isPending: isLoadingJobAds,
    isSuccess: isSuccessJobAds,
    isError: isErrorJobAds,
  } = useQuery({
    enabled: !!currentUser && !!careerCenter && !!currentSearchConfig,
    gcTime: QUERY_CACHE_TIME,
    placeholderData: previousData => (!currentUser ? previousData : undefined),
    queryFn: () =>
      getJobAdsFromAlgolia({
        algoliaJobAdsRequest,
        computeResultsMethod: response =>
          computeJobAdsResultFromAlgolia({ response, withSavedSearch: !!algoliaJobAdsRequest.savedSearchQuery }),
        searchEngineOptions: {
          isDirectAlgoliaConnectionEnabled,
          isMultiHostConnectionEnabled,
          isSearchProxyV3ConnectionEnabled,
          userToken: null,
        },
      }),
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getJobAdsFromAlgoliaQueryKeys(algoliaJobAdsRequest.mainQuery),
    staleTime: QUERY_STALE_TIME,
  })

  return {
    dataJobAds: data,
    isErrorJobAds,
    isLoadingJobAds,
    isSuccessJobAds,
  }
}
